// ═══════════════════════════════════════════════════════════════════
// WHICH ORGANIZATION THIS ACCOUNT WORKED IN LAST, ON THIS BROWSER
// (owner decision 2026-10-19, «4A»: somebody with an organization role lands
// in the portal after signing in, in the organization they used last).
//
// IT IS A PREFERENCE, NOT AUTHORITY — the same standing as the stored locale
// (`i18n/localePreference.ts`). It only says which of the scopes the SERVER
// offered to pre-select: `clubSession.selectScope` ignores an id that is not
// on offer, and every request is re-decided by the API regardless. Losing it
// (a private window, cleared site data, another machine) means one extra click
// in the organization picker, nothing more — so every access is wrapped and a
// failure is silent.
//
// Keyed by ACCOUNT, so two people sharing a browser do not inherit each
// other's last organization. The tenant id is not a secret; it travels in
// every request header already.
// ═══════════════════════════════════════════════════════════════════

const PREFIX = 'starpoint.lastScope.';

export function readLastScope(accountId: string | null | undefined): string | null {
  if (!accountId) return null;
  try {
    const raw = globalThis.localStorage?.getItem(PREFIX + accountId);
    return raw && raw.trim() ? raw : null;
  } catch {
    return null;
  }
}

export function writeLastScope(
  accountId: string | null | undefined,
  tenantId: string | null,
): void {
  if (!accountId || !tenantId) return;
  try {
    globalThis.localStorage?.setItem(PREFIX + accountId, tenantId);
  } catch {
    /* a preference that cannot be stored is still usable for this session */
  }
}

/**
 * Where a fresh sign-in should land (decision 4A).
 *
 *   'personal'  — no organization to act in (none, or the scope list could
 *                 not be read): «Min StarPoint», the player surface. Sending
 *                 such an account to the portal would meet «Ingen tilgang».
 *   'portal'    — at least one organization; `preselect` is the remembered
 *                 one when it is still on offer, else null (one scope is
 *                 selected by the session itself; several open the picker).
 */
export function signInLanding(
  scopes: readonly { tenantId: string }[],
  remembered: string | null,
): { surface: 'personal' } | { surface: 'portal'; preselect: string | null } {
  if (scopes.length === 0) return { surface: 'personal' };
  const stillOffered = remembered && scopes.some((s) => s.tenantId === remembered);
  return { surface: 'portal', preselect: stillOffered ? remembered : null };
}
